import { useEffect, useState } from 'react';
import type { GridSize, Table, Wall } from '../types';
import { areTablesEqual, mergeUpdatedTable } from '../utils/tableState';
import { useI18n } from '../i18n/I18nContext';

type SaveScenarioFn = (
  silent?: boolean,
  overrides?: {
    tables?: Table[];
    walls?: Wall[];
  },
  options?: {
    historyDescription?: string | null;
  },
) => void;

interface UseTableActionsOptions {
  tables: Table[];
  walls: Wall[];
  gridSize: GridSize;
  setTables: React.Dispatch<React.SetStateAction<Table[]>>;
  pushHistory: (nextTables: Table[], nextWalls: Wall[], description?: string) => void;
  saveScenario: SaveScenarioFn;
}

function uniqueId() {
  return '_' + Math.random().toString(36).substr(2, 9);
}

export function useTableActions({
  tables,
  walls,
  gridSize,
  setTables,
  pushHistory,
  saveScenario,
}: UseTableActionsOptions) {
  const { t } = useI18n();
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [showTableModal, setShowTableModal] = useState<boolean>(false);

  useEffect(() => {
    if (!showTableModal || !selectedTable) {
      return;
    }

    const latestTable = tables.find((table) => table.id === selectedTable.id) ?? null;
    if (!latestTable) {
      setShowTableModal(false);
      setSelectedTable(null);
      return;
    }

    setSelectedTable((current) => {
      if (!current) {
        return current;
      }
      return areTablesEqual(current, latestTable) ? current : latestTable;
    });
  }, [selectedTable, showTableModal, tables]);

  function openTableModal(table: Table): void {
    setSelectedTable(table);
    setShowTableModal(true);
  }

  function closeTableModal(): void {
    setShowTableModal(false);
    setSelectedTable(null);
  }

  function addTable(): void {
    const id = uniqueId();
    const index = tables.length + 1;
    let x = 0;
    let y = 0;

    outer: for (let r = 0; r < gridSize.rows; r++) {
      for (let c = 0; c < gridSize.cols; c++) {
        const collision = tables.some(t => c >= t.x && c < t.x + t.w && r >= t.y && r < t.y + t.h);
        if (!collision) {
          x = c;
          y = r;
          break outer;
        }
      }
    }

    const newTable: Table = {
      id,
      name: `Table ${index}`,
      x,
      y,
      w: 1,
      h: 1,
      person: '',
      products: [],
    };

    const updated = [...tables, newTable];
    const description = t('table.addedHistory', { name: newTable.name });
    setTables(updated);
    pushHistory(updated, walls, description);
    saveScenario(true, { tables: updated }, { historyDescription: description });
  }

  function updateTable(updatedTable: Table, options?: { recordHistory?: boolean }): void {
    const shouldRecordHistory = options?.recordHistory !== false;
    const updated = tables.map(table => (
      table.id === updatedTable.id ? mergeUpdatedTable(table, updatedTable) : table
    ));
    setTables(updated);
    const description = t('table.editedHistory', { name: updatedTable.name });
    if (shouldRecordHistory) {
      pushHistory(updated, walls, description);
    }
    saveScenario(true, { tables: updated }, { historyDescription: shouldRecordHistory ? description : null });
  }

  function deleteTable(table: Table): void {
    const updated = tables.filter(t => t.id !== table.id);
    const description = t('table.deletedHistory', { name: table.name });
    setTables(updated);
    pushHistory(updated, walls, description);
    saveScenario(true, { tables: updated }, { historyDescription: description });
  }

  function reorderTables(updated: Table[]): void {
    const description = t('table.reorderedHistory');
    setTables(updated);
    pushHistory(updated, walls, description);
    saveScenario(true, { tables: updated }, { historyDescription: description });
  }

  return {
    selectedTable,
    showTableModal,
    openTableModal,
    closeTableModal,
    addTable,
    updateTable,
    deleteTable,
    reorderTables,
  };
}
